import { onUnmounted } from "vue";
import { SocketStock } from "@/helpers/models/stock/socket_stock.model";
import { StockSocketTransferDTO } from "@/helpers/models/stock/stock_socket_transfer.dto";
import utilService from "@/services/util.service";
import { trackError, trackEvent } from "@/helpers/tracking.helpers";
import { SocketEvents, TrackingEvents } from "@/helpers/enum";
import { SocketStateManager } from "@/helpers/socket/socket-state.singleton";
import SocketConnectionBuilder from "@/helpers/socket/socket-connection.builder";
import { parsedData } from "@/helpers";
import {
  DEBOUNCE_ON_INPUT,
  LISTEN_STOCK_FROM_STOCKIDS_TIMEOUT,
  SEARCH_RESULTS_LIMIT,
  STOCK_SOCKET_LOGGING_ENABLED,
  SOCKET_LOGGING_ENABLED
} from "@/helpers/constants";
import { scheduleNatsSync } from "./pricing_socket.composable";
import { mergeApiStockRow } from "@/helpers/live-price.helpers";

const state = SocketStateManager.getInstance();

const trackSocketError = (error: any, context: string, metadata?: Record<string, any>) => {
  trackError(error, {
    context: `Socket:${context}`,
    socketConnected: state.socket?.connected,
    ...metadata
  });
};

const trackSocketEvent = (eventName: string, metadata?: Record<string, any>) => {
  trackEvent(eventName, {
    socketConnected: state.socket?.connected,
    ...metadata
  });
};

// Add debounce function at the top of your composable (outside useStockSocket)
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };

    const callNow = immediate && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) func(...args);
  };
};
// Add this helper method to execute the actual request





export const useStockSocket = () => {
  let debounceTimeout: NodeJS.Timeout | null = null;
  const loggingEnabled = STOCK_SOCKET_LOGGING_ENABLED;
  const socketBuilder = SocketConnectionBuilder.create();
  const GET_USERS_DEBOUNCE_DELAY = 100;
  const GET_STOCKS_DEBOUNCE_DELAY = 100;
  const IMMEDIATE_REQUEST_CACHE_DURATION = 5000; // 5 seconds cache


  const executeUserIdsRequest = async (userIds: string[]): Promise<any> => {
    if (!userIds.length) return [];

    return new Promise((resolve) => {
      socketBuilder.emitWithCallback(
        SocketEvents.users,
        userIds,
        (response: any, error?: any) => {
          if (error) {
            trackSocketError(error, 'GetUsersByID', { userIds });
            resolve([]);
          } else {
            if (response && response.length > 0) {
              response.forEach((data: any) => {
                state.userDataInfo.value[data._id] = data;
                // Mark these IDs as processed
                state.processedUserIds.add(data._id);
              });
            }
            resolve(response);
          }
        }
      );
    });
  };


  const executeStocksRequest = async (stockIds: string[]): Promise<any> => {
    if (!stockIds.length) return [];

    // Convert string IDs back to StockSocketTransferDTO objects
    const stockObjects = stockIds.map(stockId =>
      new StockSocketTransferDTO(stockId, false) // showChart will be updated in checkStockExistence
    );

    return new Promise((resolve) => {
      socketBuilder.emitWithCallback(
        SocketEvents.getstocksdata,
        { stocks: stockObjects },
        (response: any, error?: any) => {
          if (error) {
            trackSocketError(error, TrackingEvents.stockDataRequest, {
              stockIds: stockIds,
              stockObjectsCount: stockObjects.length
            });
            resolve([]);
          } else {
            handleStockUpdate(response);
            // Mark these stock IDs as processed
            stockIds.forEach(stockId => state.processedStockIds.add(stockId));
            scheduleNatsSync();
            resolve(response);
          }
        }
      );
    });
  };



  const debouncedGetUsersByID = debounce(async () => {
    if (state.pendingUserIdsBatch.size === 0) return;

    // Filter out already processed user IDs
    const userIdsToFetch = Array.from(state.pendingUserIdsBatch).filter(
      userId => !state.processedUserIds.has(userId)
    );

    state.pendingUserIdsBatch.clear();

    if (userIdsToFetch.length === 0) {
      if (loggingEnabled) {
        console.log("GetUsersByID: All user IDs already processed, skipping request");
      }
      return;
    }

    if (loggingEnabled) {
      console.log("GetUsersByID: Debounced call executing for", userIdsToFetch.length, "user IDs");
    }

    await executeUserIdsRequest(userIdsToFetch);
  }, GET_USERS_DEBOUNCE_DELAY);

  const debounceOnInput = (func: (...args: any[]) => void, ms: number = DEBOUNCE_ON_INPUT) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    debounceTimeout = setTimeout((e: any) => func(e), ms);
    state.addTimeout(debounceTimeout);
  };

  const setupSocketListeners = async () => {
    if (!state.socket) return;

    const onUserUpdate = (data: any) => handleUserUpdate(data);
    const onPredictionUpdate = (data: any) => handlePredictionUpdate(data);
    const onPortfolioSummaryUpdate = (data: any) => {
      try {
        const parsed = parsedData(data);
        state.portfolioSummaryData.value = parsed;
      } catch (error) {
        trackSocketError(error, 'PortfolioSummaryDataParsing', { rawData: data });
      }
    };
    const onPortfolioCardUpdate = (data: any) => {
      try {
        const parsed = parsedData(data);
        state.portfoliosCardData.value = parsed;
        trackSocketEvent(TrackingEvents.portfolioCardSubscribed);
      } catch (error) {
        trackSocketError(error, TrackingEvents.portfolioCardSubscribed, { rawData: data });
      }
    };
    const onPortfolioOverviewUpdate = (data: any) => {
      try {
        const parsed = parsedData(data);
        state.portfolioOverviewData.value = parsed;
      } catch (error) {
        trackSocketError(error, 'PortfolioOverviewDataParsing', { rawData: data });
      }
    };

    state.socket.on(SocketEvents.users, onUserUpdate);
    state.socket.on(SocketEvents.getPredictionsByID, onPredictionUpdate);
    state.socket.on(SocketEvents.portfolioSummaryUpdate, onPortfolioSummaryUpdate);
    state.socket.on(SocketEvents.portfolioCardUpdate, onPortfolioCardUpdate);
    state.socket.on(SocketEvents.portfolioOverviewUpdate, onPortfolioOverviewUpdate);

    state.addCleanup(() => {
      state.socket?.off(SocketEvents.users, onUserUpdate);
      state.socket?.off(SocketEvents.getPredictionsByID, onPredictionUpdate);
      state.socket?.off(SocketEvents.portfolioSummaryUpdate, onPortfolioSummaryUpdate);
      state.socket?.off(SocketEvents.portfolioCardUpdate, onPortfolioCardUpdate);
      state.socket?.off(SocketEvents.portfolioOverviewUpdate, onPortfolioOverviewUpdate);
    });
  };

  const connectToSocket = async () => {
    if (loggingEnabled) console.log("Stock: Socket connection Initiated..");

    if (state.socket?.connected) {
      if (loggingEnabled) console.log("Stock: Socket already connected");
      return;
    }

    if (!state.socket || !state.socket.connected) {
      if (loggingEnabled) console.log("Stock: Initializing new socket connection");

      state.socket = socketBuilder
        .on(SocketEvents.connect, async () => {
          if (loggingEnabled) console.log("Stock: Socket connected successfully");
          trackSocketEvent(TrackingEvents.socketConnected, {
            url: utilService.getLiveHostURL()
          });
          await setupSocketListeners();
          getUsersByID();
          getPredictionsByID();
        })
        .on(SocketEvents.disconnect, (reason) => {
          trackSocketEvent(TrackingEvents.socketDisconnected, { reason });
        })
        .on(SocketEvents.connect_error, (error) => {
          trackSocketError(error, TrackingEvents.socketConnectionError);
        })
        .on(SocketEvents.error, (error) => {
          trackSocketError(error, TrackingEvents.socketError);
        })
        .connect();
    }
  };

  const disconnectFromSocket = () => {
    try {
      state.socket?.removeAllListeners();
      state.socket?.close();
      state.socket = null;
      trackSocketEvent(TrackingEvents.socketManualDisconnect);
      console.log('Stock Socket Disconnected..')
    } catch (error) {
      trackSocketError(error, 'DisconnectionError');
    }
  };

  // const listenStockFromStockIds = async () => {
  //   if (!state.socket?.connected || document.hidden || !state.stockIds.length) {
  //     if (loggingEnabled) {
  //       console.log("Stock: Socket not connected or no stock IDs to listen to", {
  //         socket: state.socket?.connected,
  //         document: document.hidden,
  //         stockIds: state.stockIds.length
  //       });
  //     }
  //     // const timeout = setTimeout(async () => await listenStockFromStockIds(), LISTEN_STOCK_FROM_STOCKIDS_TIMEOUT);
  //     // state.addTimeout(timeout);
  //     return;
  //   }

  //   if (loggingEnabled) {
  //     console.log("Listening for stock updates for IDs:", state.stockIds.length);
  //   }

  //   socketBuilder.emitWithCallback(
  //     SocketEvents.getstocksdata,
  //     { stocks: state.stockIds },
  //     (response: any, error?: any) => {
  //       if (error) {
  //         trackSocketError(error, TrackingEvents.stockDataRequest, {
  //           stockIds: state.stockIds
  //         });
  //       } else {
  //         handleStockUpdate(response);
  //       }

  //       // if (loggingEnabled) console.log("Stock: listenStockFromStockIds completed");
  //       // const timeout = setTimeout(async () => await listenStockFromStockIds(), LISTEN_STOCK_FROM_STOCKIDS_TIMEOUT);
  //       // state.addTimeout(timeout);
  //     }
  //   );
  // };
  const debouncedGetStocks = debounce(async () => {
    if (state.pendingStockIdsBatch.size === 0) return;

    // Filter out already processed stock IDs
    const stockIdsToFetch = Array.from(state.pendingStockIdsBatch).filter(
      stockId => !state.processedStockIds.has(stockId)
    );

    state.pendingStockIdsBatch.clear();

    if (stockIdsToFetch.length === 0) {
      if (loggingEnabled) {
        console.log("GetStocks: All stock IDs already processed, skipping request");
      }
      return;
    }

    if (loggingEnabled) {
      console.log("GetStocks: Debounced call executing for", stockIdsToFetch.length, "stock IDs");
    }

    await executeStocksRequest(stockIdsToFetch);
  }, GET_STOCKS_DEBOUNCE_DELAY);

  const listenStockFromStockIds = async () => {
    if (!state.socket?.connected || document.hidden || !state.stockIds.length) {
      if (loggingEnabled) {
        console.log("Stock: Socket not connected or no stock IDs to listen to", {
          socket: state.socket?.connected,
          document: document.hidden,
          stockIds: state.stockIds.length
        });
      }
      return;
    }

    // Filter out stock IDs that are already in memory AND not in pending batch
    const newStockIds = state.stockIds
      .filter(stockObj => !state.stocksData[stockObj.stockId])
      .map(stockObj => stockObj.stockId)
      .filter(stockId => !state.processedStockIds.has(stockId));

    if (newStockIds.length === 0) {
      if (loggingEnabled) {
        console.log("Stock: All stocks already processed or in memory, skipping", {
          totalStocks: state.stockIds.length,
          inMemory: Object.keys(state.stocksData).length,
          processed: state.processedStockIds.size
        });
      }
      return;
    }

    // Add to pending batch
    newStockIds.forEach(stockId => state.pendingStockIdsBatch.add(stockId));

    if (loggingEnabled) {
      console.log("GetStocks: Added to batch", newStockIds.length, "new stock IDs, total pending:", state.pendingStockIdsBatch.size);
    }

    // Clear existing timeout and set new one
    if (state.getStocksDebounceTimeout) {
      clearTimeout(state.getStocksDebounceTimeout);
    }

    state.getStocksDebounceTimeout = setTimeout(() => {
      debouncedGetStocks();
      state.getStocksDebounceTimeout = null;
    }, GET_STOCKS_DEBOUNCE_DELAY);
  };

  // const listenStockFromStockIds = async () => {
  //   if (!state.socket?.connected || document.hidden || !state.stockIds.length) {
  //     if (loggingEnabled) {
  //       console.log("Stock: Socket not connected or no stock IDs to listen to", {
  //         socket: state.socket?.connected,
  //         document: document.hidden,
  //         stockIds: state.stockIds.length
  //       });
  //     }
  //     return;
  //   }

  //   // Filter out stock IDs that are already in memory
  //   const newStockIds = state.stockIds.filter(stockObj =>
  //     !state.stocksData[stockObj.stockId]
  //   );

  //   // If all stocks are already in memory, no need to make the call
  //   if (newStockIds.length === 0) {
  //     if (loggingEnabled) {
  //       console.log("Stock: All stocks already in memory, skipping socket call", {
  //         totalStocks: state.stockIds.length
  //       });
  //     }
  //     return;
  //   }

  //   if (loggingEnabled) {
  //     console.log("Listening for stock updates for new IDs:", newStockIds.length,
  //       "Already in memory:", state.stockIds.length - newStockIds.length);
  //   }

  //   socketBuilder.emitWithCallback(
  //     SocketEvents.getstocksdata,
  //     { stocks: newStockIds },
  //     (response: any, error?: any) => {
  //       if (error) {
  //         trackSocketError(error, TrackingEvents.stockDataRequest, {
  //           stockIds: newStockIds,
  //           originalStockIdsCount: state.stockIds.length,
  //           newStockIdsCount: newStockIds.length
  //         });
  //       } else {
  //         handleStockUpdate(response);
  //       }
  //     }
  //   );
  // };
  const handleStockUpdate = (data: any) => {
    try {
      data?.forEach((stock: any) => {
        if (!stock?.stockId) return;
        state.stocksData[stock.stockId] = mergeApiStockRow(
          state.stocksData[stock.stockId],
          stock
        );
        const index = state.stocks.findIndex((x: any) => x.stockId === stock.stockId);
        if (index === -1) {
          state.stocks.push(stock);
        } else {
          state.stocks[index] = mergeApiStockRow(state.stocks[index], stock);
        }
      });
      trackSocketEvent(TrackingEvents.stockUpdateProcessing);
    } catch (error) {
      trackSocketError(error, TrackingEvents.stockUpdateProcessingError);
    }
  };

  // Add methods to clear stock cache when needed
  const clearProcessedStockIds = (stockIds?: string[]) => {
    if (stockIds) {
      stockIds.forEach(id => state.processedStockIds.delete(id));
    } else {
      state.processedStockIds.clear();
    }

    if (loggingEnabled) {
      console.log("GetStocks: Cleared processed stock IDs cache", stockIds ? `for ${stockIds.length} IDs` : 'completely');
    }
  };

  const invalidateStockIds = (stockIds: string[]) => {
    stockIds.forEach(id => state.processedStockIds.delete(id));

    if (loggingEnabled) {
      console.log("GetStocks: Invalidated stock IDs cache for", stockIds.length, "IDs");
    }
  };

  const addStockId = (stockObj: StockSocketTransferDTO) => {
    try {
      if (stockObj) {
        checkStockExistence(stockObj.stockId!, true);
      }
    } catch (error) {
      trackSocketError(error, TrackingEvents.stockIdError, { stockObj: stockObj.stockId });
    }
  };

  const clearStockIds = () => {
    //state.stockIds = [];
  };

  const getDataByStockId = (stockId: string) => {
    try {
      if (!stockId) return null;
      return state.stocksData[stockId] || null;
    } catch (error) {
      trackSocketError(error, TrackingEvents.stockDataRequest, { stockId });
      return null;
    }
  };


  const checkStockExistence = (stockId: string, showChart: boolean = false) => {
    try {
      if (!stockId) return null;

      const index = state.stockIds.findIndex((x) => x.stockId == stockId);
      if (index === -1) {
        state.stockIds.push(new StockSocketTransferDTO(stockId, showChart));
        scheduleNatsSync();
      } else if (state.stockIds[index].showTrendChart != showChart) {
        state.stockIds[index].showTrendChart = showChart;
      }
      listenStockFromStockIds();
      return true;
    } catch (error) {
      trackSocketError(error, 'CheckStockExistence', { stockId, showChart });
      return null;
    }
  };

  // const checkStockExistence = (stockId: string, showChart: boolean = false) => {
  //   try {
  //     if (!stockId) return null;

  //     const index = state.stockIds.findIndex((x) => x.stockId == stockId);
  //     if (index === -1) {
  //       state.stockIds.push(new StockSocketTransferDTO(stockId, showChart));
  //     } else if (state.stockIds[index].showTrendChart != showChart) {
  //       state.stockIds[index].showTrendChart = showChart;
  //     }
  //     listenStockFromStockIds();
  //     return true;
  //   } catch (error) {
  //     trackSocketError(error, 'CheckStockExistence', { stockId, showChart });
  //     return null;
  //   }
  // };

  const handleUserUpdate = (response: any) => {
    try {
      if (loggingEnabled) {
        console.log("User update received:", response);
      }
      response?.forEach((data: any) => {
        state.userDataInfo.value[data._id] = data;
      });
    } catch (error) {
      trackSocketError(error, 'UserUpdateProcessing', { response });
    }
  };

  // const getUsersByID = async (userIds: string[] = []): Promise<any> => {
  //   const userIdsSet = userIds?.length ? userIds : state.userIds.value;
  //   return new Promise((resolve) => {
  //     socketBuilder.emitWithCallback(
  //       SocketEvents.users,
  //       userIdsSet,
  //       (response: any, error?: any) => {
  //         if (error) {
  //           trackSocketError(error, 'GetUsersByID', { userIds: userIdsSet });
  //           resolve([]);
  //         } else {
  //           if (!userIds?.length && response && response.length > 0) {
  //             response.forEach((data: any) => {
  //               state.userDataInfo.value[data._id] = data;
  //             });
  //           }
  //           resolve(response);
  //         }
  //       }
  //     );
  //   });
  // };




  const getUsersByID = async (userIds: string[] = []): Promise<any> => {
    // ALWAYS use debouncing for both cases
    const idsToProcess = userIds?.length > 0 ? userIds : state.userIds.value;

    if (idsToProcess.length === 0) {
      if (loggingEnabled) {
        console.log("GetUsersByID: No user IDs to fetch");
      }
      return [];
    }

    // Filter out already processed IDs before adding to batch
    const newUserIds = idsToProcess.filter(userId => !state.processedUserIds.has(userId));

    if (newUserIds.length === 0) {
      if (loggingEnabled) {
        console.log("GetUsersByID: All user IDs already processed");
      }
      return [];
    }

    // Add to pending batch in singleton
    newUserIds.forEach(id => state.pendingUserIdsBatch.add(id));

    if (loggingEnabled) {
      console.log("GetUsersByID: Added to batch", newUserIds.length, "new IDs, total pending:", state.pendingUserIdsBatch.size);
    }

    // Clear existing timeout and set new one
    if (state.getUserByIdDebounceTimeout) {
      clearTimeout(state.getUserByIdDebounceTimeout);
    }

    state.getUserByIdDebounceTimeout = setTimeout(() => {
      debouncedGetUsersByID();
      state.getUserByIdDebounceTimeout = null;
    }, GET_USERS_DEBOUNCE_DELAY);

    return [];
  };

  const clearProcessedUserIds = (userIds?: string[]) => {
    if (userIds) {
      userIds.forEach(id => state.processedUserIds.delete(id));
    } else {
      state.processedUserIds.clear();
    }

    if (loggingEnabled) {
      console.log("GetUsersByID: Cleared processed user IDs cache", userIds ? `for ${userIds.length} IDs` : 'completely');
    }
  };

  // Add method to manually mark user IDs as needing refresh
  const invalidateUserIds = (userIds: string[]) => {
    userIds.forEach(id => state.processedUserIds.delete(id));

    if (loggingEnabled) {
      console.log("GetUsersByID: Invalidated user IDs cache for", userIds.length, "IDs");
    }
  };

  const clearUserIds = () => {
    state.userIds.value = [];
    state.userDataInfo.value = {};
  };

  const handlePredictionUpdate = (response: any) => {
    try {
      if (loggingEnabled) {
        console.log("Prediction update received:", response);
      }
      response?.forEach((data: any) => {
        state.predicitionDataInfo.value[data.predictionId] = data;
      });
      trackSocketEvent(TrackingEvents.predictionHandlePredictionUpdate);
    } catch (error) {
      trackSocketError(error, TrackingEvents.predictionHandlePredictionUpdate);
    }
  };

  const getPredictionsByID = async () => {
    if (!state.socket?.connected || !state.predictionIds.value.length) return;

    socketBuilder.emitWithCallback(
      SocketEvents.getPredictionsByID,
      state.predictionIds.value,
      (response: any, error?: any) => {
        if (error) {
          trackSocketError(error, TrackingEvents.predictionDataReceivedViaId);
        } else {
          response?.forEach((data: any) => {
            state.predicitionDataInfo.value[data.predictionId] = data;
          });
          trackSocketEvent(TrackingEvents.predictionDataReceivedViaId);
        }
      }
    );
  };

  const clearPredictionIds = () => {
    state.predictionIds.value = [];
    state.predicitionDataInfo.value = {};
  };

  const searchStocks = async (keyword: string, fetchAll: boolean = false): Promise<SocketStock[]> => {

    if (!keyword?.trim() && !fetchAll) {
      console.log("SearchStocks: Empty keyword provided");
      return [];
    }

    return new Promise((resolve) => {
      try {
        if (fetchAll) {
          resolve(state.stocks);
          return;
        }
        // const keywordLower = keyword.toLowerCase().trim();
        // const localResults = state.stocks.filter(stock =>
        //   stock.stockId?.toLowerCase().includes(keywordLower) ||
        //   stock.name?.toLowerCase().includes(keywordLower)
        // ).slice(0, SEARCH_RESULTS_LIMIT);

        // if (localResults.length > 0) {
        //   if (loggingEnabled) console.log("SearchStocks: Found in local cache", localResults);
        //   resolve(localResults as SocketStock[]);
        //   return;
        // }

        // if (loggingEnabled) console.log("SearchStocks: No local results, querying socket...");

        socketBuilder.emitWithCallback(
          SocketEvents.searchStockByKeyword,
          keyword,
          (response: any, error?: any) => {
            if (error) {
              if (loggingEnabled) console.error("SearchStocks: Error occurred:", error);
              trackSocketError(error, 'SearchResultsProcessing', {
                keyword,
                socketConnected: state.socket?.connected,
                socketState: state.socket?.connected ? 'connected' : 'disconnected'
              });
              resolve([]);
              return;
            }

            const results = Array.isArray(response)
              ? response.slice(0, SEARCH_RESULTS_LIMIT)
              : [];

            results.forEach(stock => {
              if (stock?.stockId) {
                const index = state.stocks.findIndex(x => x.stockId === stock.stockId);
                if (index === -1) {
                  state.stocksData[stock.stockId] = stock;
                  state.stocks.push(stock);
                }
              }
            });

            if (loggingEnabled) console.log("SearchStocks: Processed results from socket:", results);
            resolve(results);
          },
          false
        );
      } catch (error) {
        if (loggingEnabled) console.error("SearchStocks: Error occurred:", error);
        trackSocketError(error, 'SearchResultsProcessing', {
          keyword,
          socketConnected: state.socket?.connected,
          socketState: state.socket?.connected ? 'connected' : 'disconnected'
        });
        resolve([]);
      }
    });
  };

  const searchStocksOnTrade = (keyword: string, market: string = 'NASDAQ'): Promise<SocketStock[]> => {
    return new Promise((resolve) => {
      if (!keyword?.trim()) {
        if (loggingEnabled) console.log("SearchStocksOnTrade: Empty keyword provided");
        resolve([]);
        return;
      }

      try {
        const info = {
          keyword: keyword.trim(),
          market: market
        };

        if (loggingEnabled) console.log("SearchStocksOnTrade: Searching for", info);

        socketBuilder.emitWithCallback(
          SocketEvents.searchStockByKeywordAndMarket,
          info,
          (data: any, error?: any) => {
            if (error) {
              trackSocketError(error, 'SearchRequest', { keyword, market });
              resolve([]);
              return;
            }

            try {
              const results = Array.isArray(data) ? data.slice(0, SEARCH_RESULTS_LIMIT) : [];

              results.forEach(stock => {
                if (stock?.stockId) {
                  const index = state.stocks.findIndex(x => x.stockId === stock.stockId);
                  if (index === -1) {
                    state.stocksData[stock.stockId] = stock;
                    state.stocks.push(stock);
                  }
                }
              });

              if (loggingEnabled) console.log("SearchStocksOnTrade: Results found", results);
              resolve(results);
            } catch (parseError) {
              trackSocketError(parseError, 'SearchResultsProcessing', { keyword, market });
              resolve([]);
            }
          }
        );
      } catch (error) {
        trackSocketError(error, 'SearchInitialization', { keyword, market });
        resolve([]);
      }
    });
  };

  const holdingsSocketEventSubs = (params: {
    symbol: string;
    qty: number;
    avgFillPrice: number;
    side: string;
    realizedPnL: number;
    portfolioId: string;
  }) => {
    socketBuilder.emitWithCallback(
      SocketEvents.portfolioHoldingsUpdate,
      params,
      (response: any, error?: any) => {
        console.log('PORTFOLIO HOLDINGS SUBSCRIPTION: ', response);
        if (error) {
          console.log('PORTFOLIO HOLDINGS ERROR: ', response);
          trackSocketError(error, TrackingEvents.holdingsSubscriptionResponse, {
            portfolioId: params.portfolioId
          });
        } else {
          trackSocketEvent(TrackingEvents.holdingsSubscriptionResponse, {
            portfolioId: params.portfolioId
          });
        }
      },
      false
    );
  };

  const holdingsSocketEventUnsubs = (params: { portfolioId: string }) => {
    socketBuilder.emitWithCallback(
      SocketEvents.portfolioHoldingsUpdateunSubscribe,
      { symbols: [], portfolioId: params.portfolioId, clearAll: true },
      (response: any, error?: any) => {
        console.log('PORTFOLIO HOLDINGS UNSUBSCRIPTION: ', response);
        if (error) {
          trackSocketError(error, TrackingEvents.holdingsUnsubscriptionResponse, {
            portfolioId: params.portfolioId
          });
        } else {
          trackSocketEvent(TrackingEvents.holdingsUnsubscriptionResponse, {
            portfolioId: params.portfolioId
          });
        }
      },
      false
    );
  };

  const portfolioSummarySubs = async ({
    _id,
    symbol,
    openQty,
    closeQty,
    avgOpenPrice,
    side,
    totalReturn,
    portfolioId,
  }) => {
    try {
      await portfolioSummaryUnsubs(portfolioId);
      socketBuilder.emitWithCallback(
        SocketEvents.portfolioSummaryUpdate,
        {
          _id,
          symbol,
          openQty,
          closeQty,
          avgOpenPrice,
          side,
          totalReturn,
          portfolioId,
        },
        (response: any, error?: any) => {
          console.log('PORTFOLIO SUMMARY RESPONSE', response);
          if (error) {
            trackSocketError(
              error,
              TrackingEvents.portfolioSummarySubscriptionResponse,
              { portfolioId }
            );
          } else {
            trackSocketEvent(
              TrackingEvents.portfolioSummarySubscriptionResponse,
              {
                portfolioId,
              }
            );
          }
        },
        false
      );
    } catch (error) {
      trackSocketError(
        error,
        TrackingEvents.portfolioSummarySubscriptionResponse,
        { portfolioId }
      );
    }
  };

  const portfolioSummaryUnsubs = async (portfolioId): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        socketBuilder.emitWithCallback(
          SocketEvents.portfolioSummaryUpdateUnSubscribe,
          { portfolioId, _ids: [], clearAll: true },
          (response: any, error?: any) => {
            if (error) {
              trackSocketError(error, TrackingEvents.portfolioSummaryUnsubscriptionResponse);
              resolve(false);
            } else {
              trackSocketEvent(TrackingEvents.portfolioSummaryUnsubscriptionResponse);
              resolve(true);
            }
          }
        );
      } catch (error) {
        trackSocketError(error, TrackingEvents.portfolioSummaryUnsubscriptionResponse);
        resolve(false);
      }
    });
  };

  const portfolioSummaryListener = () => {
    if (!state.socket) return;

    state.socket.on(SocketEvents.portfolioSummaryUpdate, (data: any) => {
      try {
        const parsed = parsedData(data);
        state.portfolioSummaryData.value = parsed;
      } catch (error) {
        trackSocketError(error, 'PortfolioSummaryDataParsing', { rawData: data });
      }
    });
  };

  const portfolioCardSubs = (portfolioIds: any[]) => {
    socketBuilder.emitWithCallback(
      SocketEvents.portfolioCardUpdate,
      { portfolioIds },
      (response: any, error?: any) => {
        console.log('PORTFOLIO CARD SUBSCRIPTION', response);
        if (error) {
          console.log('PORTFOLIO CARD SUBSCRIPTION ERROR', error);
          trackSocketError(error, TrackingEvents.portfolioCardSubscriptionResponse, { portfolioIds });
        } else {
          trackSocketEvent(TrackingEvents.portfolioCardSubscriptionResponse, { portfolioIds });
        }
      }
    );
  };

  const portfolioCardUnsubs = (): Promise<boolean> => {
    return new Promise((resolve) => {
      socketBuilder.emitWithCallback(
        SocketEvents.portfolioCardUpdateUnsubscribe,
        { portfolioIds: [], clearAll: true },
        (response: any, error?: any) => {
          console.log('PORTFOLIO CARD UNSUBSCRIPTION', response);
          if (error) {
            console.log('PORTFOLIO CARD UNSUBSCRIPTION ERROR', error);
            trackSocketError(error, TrackingEvents.portfolioCardUnsubscriptionResponse);
            resolve(false);
          } else {
            trackSocketEvent(TrackingEvents.portfolioCardUnsubscriptionResponse);
            resolve(true);
          }
        }
      );
    });
  };

  const portfolioCardListener = () => {
    if (!state.socket) return;
    state.socket.on(SocketEvents.portfolioCardUpdate, (data: any) => {
      try {
        console.log('PORTFOLIO CARD LISTENER', parsedData(data));
        const parsed = parsedData(data);
        state.portfoliosCardData.value = parsed;
        trackSocketEvent(TrackingEvents.portfolioCardSubscribed);
      } catch (error) {
        console.log('PORTFOLIO CARD LISTENER ERROR', { rawData: data });
        trackSocketError(error, TrackingEvents.portfolioCardSubscribed, { rawData: data });
      }
    });
  };

  const portfolioOverviewSubs = async ({ portfolioId }) => {
    try {
      await portfolioOverviewUnsubs();
      socketBuilder.emitWithCallback(
        SocketEvents.portfolioOverviewUpdate,
        { portfolioId },
        (response: any, error?: any) => {
          console.log('PORTFOLIO OVERVIEW SUBSCRIPTION', response);
          if (error) {
            console.log('PORTFOLIO OVERVIEW SUBSCRIPTION ERROR', error);
            trackSocketError(error, TrackingEvents.portfolioOverviewSubscriptionResponse, { portfolioId });
          } else {
            trackSocketEvent(TrackingEvents.portfolioOverviewSubscriptionResponse, { portfolioId });
          }
        },
        false
      );
    } catch (error) {
      trackSocketError(error, TrackingEvents.portfolioOverviewSubscriptionResponse, { portfolioId });
    }
  };

  const portfolioOverviewUnsubs = (): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        socketBuilder.emitWithCallback(
          SocketEvents.portfolioOverviewUpdateUnsubscribe,
          { portfolioIds: [], clearAll: true },
          (response: any, error?: any) => {
            console.log('PORTFOLIO OVERVIEW UNSUBSCRIPTION', response);
            if (error) {
              console.log('PORTFOLIO OVERVIEW UNSUBSCRIPTION ERROR', error);
              trackSocketError(error, TrackingEvents.portfolioOverviewUnsubscriptionResponse);
              resolve(false);
            } else {
              trackSocketEvent(TrackingEvents.portfolioOverviewUnsubscriptionResponse);
              resolve(true);
            }
          }
        );
      } catch (error) {
        trackSocketError(error, TrackingEvents.portfolioOverviewUnsubscriptionResponse);
        resolve(false);
      }
    });
  };

  const portfolioOverviewListener = () => {
    try {
      state.socket.on(SocketEvents.portfolioOverviewUpdate, (data) => {
        try {
          console.log('PORTFOLIO OVERVIEW LISTENER', parsedData(data));
          state.portfolioOverviewData.value = parsedData(data);
        } catch (error) {
          console.log('PORTFOLIO OVERVIEW LISTENER ERROR', { rawData: data });
          trackSocketError(error, 'PortfolioOverviewDataParsing', { rawData: data });
        }
      });
    } catch (error) {
      console.log('PORTFOLIO OVERVIEW LISTENER ERROR', { error });
      trackSocketError(error, 'PortfolioOverviewListenerSetup');
    }
  };

  onUnmounted(() => {
    if (SOCKET_LOGGING_ENABLED) console.log("Stock Socket: Unmounting")
    state.cleanup();
  });

  return {
    searchStocksOnTrade,
    socket: state.socket,
    connectToSocket,
    disconnectFromSocket,
    debounceOnInput,
    stockIds: state.stockIds,
    addStockId,
    clearStockIds,
    getDataByStockId,
    checkStockExistence,
    searchStocks,
    userIds: state.userIds,
    userDataInfo: state.userDataInfo,
    getUsersByID,
    clearUserIds,
    predictionIds: state.predictionIds,
    predicitionDataInfo: state.predicitionDataInfo,
    getPredictionsByID,
    clearPredictionIds,
    holdingsSocketEventSubs,
    holdingsSocketEventUnsubs,
    portfolioSummarySubs,
    portfolioSummaryUnsubs,
    portfolioSummaryListener,
    portfolioSummaryData: state.portfolioSummaryData,
    portfolioCardSubs,
    portfolioCardUnsubs,
    portfolioCardListener,
    portfoliosCardData: state.portfoliosCardData,
    portfolioOverviewSubs,
    portfolioOverviewUnsubs,
    portfolioOverviewListener,
    portfolioOverviewData: state.portfolioOverviewData,

    clearProcessedUserIds: () => state.processedUserIds.clear(),
    invalidateUserIds: (userIds: string[]) => userIds.forEach(id => state.processedUserIds.delete(id)),
  };
};